/**
 * Hook for resolving the user's primary club for default-club navigation.
 *
 * Issue #692 — Club-First Onboarding (the original "first-club" placeholder)
 * Issue #1599 — Server-side last-active club tracking (this iteration)
 *
 * Resolution order:
 *
 * 1. `useMe().last_active_club_slug` if it points at a club the user is
 *    currently a member of (validated against `useClubs`, mirroring #809's
 *    stale-slug-clear pattern). The slug is JOIN-resolved at `/auth/me`
 *    from `users.last_active_club_id`, which the backend updates as a side
 *    effect of `GET /api/clubs/:slug` (and `/id/:id`) when the requester
 *    is a member of the club they're viewing.
 *
 * 2. `clubs[0].slug` — the v1.0 deterministic fallback, used when there's
 *    no last-active signal yet (new user, fresh session before any club
 *    visit, or stale pointer after club deletion / membership removal).
 *
 * 3. `null` — no slug resolved. NOTE: this conflates "clubs still
 *    loading/errored" with "genuinely zero clubs" — consumers that show
 *    onboarding UI on null must disambiguate via useClubs state themselves
 *    (HomeRedirect does: ZeroClubWelcome only renders after the clubs
 *    query successfully resolves empty — #2323, PR #2331 review round 2).
 *
 * Why server-side instead of localStorage: React Native (Expo) has no
 * `localStorage` API, and ADR-0002 commits to `lib/api/` + `hooks/` being
 * 100% shared between web and native. Server-side tracking gives both
 * surfaces the same behavior without platform-specific storage code, and
 * adds cross-device continuity as a bonus (phone PWA → laptop carries the
 * last-active club).
 */

import { useClubs } from './useClubs';
import { useMe } from './useMe';

export function usePrimaryClub(): string | null {
  const { data: clubs } = useClubs();
  const { data: me } = useMe();

  if (!clubs || clubs.length === 0) {
    return null;
  }

  // Prefer last-active when it still points at a club the user is in.
  // Validation closes the gap when the user has been removed from a club
  // (FK still references it server-side but `useClubs` won't include it).
  // The pattern mirrors `useClubNavContext`'s stale-slug check (#809).
  const lastActive = me?.last_active_club_slug ?? null;
  if (lastActive && clubs.some((c) => c.slug === lastActive)) {
    return lastActive;
  }

  // Fallback: first club by membership order (the v1.0 deterministic pick).
  return clubs[0].slug;
}
